import { RefreshCw } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ApiError } from "../lib/api.ts";
import { rememberListLink, savedListLink } from "../lib/list-link.ts";
import { SYNC_NEEDS_LINK, SYNC_OFFLINE, syncSaid, type PoiSyncResult } from "../lib/pois.ts";
import { ICON } from "../ui/index.ts";

/**
 * "Sync saved places": the trip's Google Maps saved list pulled in again, the trip's imported
 * places made to match it.
 *
 * The link sits in a field above the button, kept on this device, so the usual sync is one tap on
 * a filled-in control. Paste it once and it is there next time. It used to appear only after the
 * server had refused a bare tap for want of a link, which was one tap too many for everyone whose
 * link the server will not keep: it stores only a trip admin's, so every other member pasted the
 * same link on every sync, with nothing on screen to say so.
 *
 * An empty field sends no link at all and the server uses the one it holds, which is how a trip
 * whose admin has already saved it keeps working untouched. A link the server refused stays in the
 * field to be fixed but is not kept, or every later visit would open on a link that cannot work.
 *
 * One sync at a time: the button is disabled and says so while one runs. It needs the network and
 * says so without it, rather than queueing a sync for later. The map is not told: it fetches the
 * places afresh each time layer 2 opens, and it is not open while this is.
 */
export function PoiSync({
  tripId,
  onSync,
}: {
  tripId: string;
  onSync: (url?: string) => Promise<PoiSyncResult>;
}) {
  const [running, setRunning] = useState(false);
  /** What the field holds: this device's kept link to begin with, and whatever is typed after. */
  const [link, setLink] = useState(() => savedListLink(tripId));
  const [said, setSaid] = useState<{ text: string; problem: boolean } | null>(null);

  const sync = async () => {
    const url = link.trim() === "" ? undefined : link.trim();
    if (navigator.onLine === false) {
      setSaid({ text: SYNC_OFFLINE, problem: true });
      return;
    }
    setRunning(true);
    setSaid(null);
    try {
      const r = await onSync(url);
      // Kept only once the server has taken it, and cleared when the field was emptied.
      rememberListLink(tripId, url ?? "");
      setSaid({ text: syncSaid(r, url !== undefined), problem: false });
    } catch (e) {
      if (e instanceof ApiError && e.status === 422 && e.message === SYNC_NEEDS_LINK) {
        // Not a failure: the server has no link and neither has this device. The field is already
        // there, so this is simply what to do next.
        setSaid({ text: e.message, problem: false });
      } else {
        console.error("places: the saved list could not be synced", e);
        setSaid({ text: e instanceof ApiError ? e.message : SYNC_OFFLINE, problem: true });
      }
    } finally {
      setRunning(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void sync();
  };

  return (
    <div className="poi-sync">
      <form onSubmit={submit}>
        <label className="field">
          <span>Google Maps list link</span>
          <input
            type="url"
            inputMode="url"
            autoComplete="off"
            placeholder="https://maps.app.goo.gl/…"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            disabled={running}
          />
        </label>
        <p className="muted small">
          Kept on this device, so the next sync is one tap. Leave it empty to use the link the trip
          has saved.
        </p>
        <div className="poi-sync-actions">
          <button type="submit" className="button" disabled={running}>
            <RefreshCw {...ICON} aria-hidden="true" />
            {running ? "Syncing…" : "Sync saved places"}
          </button>
        </div>
      </form>
      {said &&
        (said.problem ? (
          <p role="alert" className="error">
            {said.text}
          </p>
        ) : (
          <p role="status" aria-label="Sync">
            {said.text}
          </p>
        ))}
    </div>
  );
}
